// frontend/src/components/MetaCubeQuantiqueVisualizer.jsx
import React, { useRef, useState } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import "../styles/metacube_quantique.css";

/* ===========
   Sphère (point d'un frame interpolé)
   =========== */
function Point({ position }) {
  return (
    <mesh position={position}>
      <sphereGeometry args={[0.05, 12, 12]} />
      <meshStandardMaterial
        color="#03dac6"
        emissive="#03dac6"
        emissiveIntensity={0.8}
      />
    </mesh>
  );
}

/* ===========
   Gestion des frames interpolées
   =========== */
function FrameInterpolator({ framesBuffer, running }) {
  const [currentFrame, setCurrentFrame] = useState(null);
  const frameIndexRef = useRef(0);
  const lastFrameRef = useRef(null);

  useFrame(() => {
    if (!running || !framesBuffer || framesBuffer.length < 2) return;

    const idx = frameIndexRef.current;
    const frameA = framesBuffer[idx];
    const frameB = framesBuffer[idx + 1];

    if (!frameA || !frameB) return;

    // interpolation linéaire simple (alpha varie avec le temps)
    const alpha = 0.5;
    const positions = frameA.positions.map((pos, i) => [
      pos[0] * (1 - alpha) + frameB.positions[i][0] * alpha,
      pos[1] * (1 - alpha) + frameB.positions[i][1] * alpha,
      pos[2] * (1 - alpha) + frameB.positions[i][2] * alpha,
    ]);

    const interpolatedFrame = {
      t: frameA.t,
      positions,
      entropy: (frameA.entropy + frameB.entropy) / 2,
    };

    setCurrentFrame(interpolatedFrame);
    lastFrameRef.current = interpolatedFrame;

    frameIndexRef.current++;
    if (frameIndexRef.current >= framesBuffer.length - 1) {
      frameIndexRef.current = 0;
    }
  });

  if (!currentFrame && lastFrameRef.current) {
    return (
      <>
        {lastFrameRef.current.positions.map((pos, i) => (
          <Point key={i} position={pos} />
        ))}
      </>
    );
  }

  if (!currentFrame) return null;

  return (
    <>
      {currentFrame.positions.map((pos, i) => (
        <Point key={i} position={pos} />
      ))}
    </>
  );
}

/* ===========
   Visualizer principal
   =========== */
export default function MetaCubeQuantiqueVisualizer({
  framesBuffer,
  running,
}) {
  return (
    <Canvas
      camera={{ position: [0, 0, 6], fov: 60 }}
      style={{ width: "100%", height: "100%" }}
    >
      <ambientLight intensity={0.6} />
      <pointLight position={[10, 10, 10]} intensity={1.2} />
      <pointLight position={[-10, -10, -10]} intensity={0.8} />

      <FrameInterpolator framesBuffer={framesBuffer} running={running} />

      <OrbitControls enablePan={false} enableZoom={true} />
      <EffectComposer>
        <Bloom intensity={1.2} luminanceThreshold={0.2} />
      </EffectComposer>
    </Canvas>
  );
}
